<script setup lang="ts">
import { computed, ref } from 'vue'
import type { GuestTesterInput, UFPMMember } from '../lib/types'
import { RANK_OPTIONS } from '../lib/ranks'

const props = defineProps<{
  members: readonly UFPMMember[]
  sessionMemberIds: readonly string[]
}>()

const emit = defineEmits<{
  'add-member': [member: UFPMMember]
  'add-guest': [input: GuestTesterInput]
}>()

const JERSEY_COLORS = ['Red', 'Orange', 'Yellow', 'Green', 'Blue', 'Purple', 'Black', 'White', 'Gray'] as const

const mode = ref<'roster' | 'freeform'>('roster')
const addSearch = ref('')

const filteredRoster = computed(() => {
  const q = addSearch.value.trim().toLowerCase()
  const inSession = new Set(props.sessionMemberIds)
  const available = props.members.filter(m => !inSession.has(m.id))
  if (!q) return available.slice(0, 12)
  return available.filter(m =>
    m.fullName?.toLowerCase().includes(q) ||
    m.lastName?.toLowerCase().includes(q) ||
    m.firstName?.toLowerCase().includes(q) ||
    m.rank?.toLowerCase().includes(q) ||
    m.organization?.toLowerCase().includes(q)
  ).slice(0, 12)
})

function rosterLabel(m: UFPMMember): string {
  const parts = [m.rank, m.lastName, m.firstName].map(v => String(v ?? '').trim()).filter(Boolean)
  return parts.join(' ') || m.fullName || 'Unknown Member'
}

function pickRoster(m: UFPMMember): void {
  emit('add-member', m)
  addSearch.value = ''
}

const guestRank = ref('')
const guestName = ref('')
const guestJerseyColor = ref('')
const guestJerseyNumber = ref('')

function submitGuest(): void {
  emit('add-guest', {
    rank: guestRank.value,
    name: guestName.value,
    jerseyColor: guestJerseyColor.value,
    jerseyNumber: guestJerseyNumber.value,
  })
  guestRank.value = ''
  guestName.value = ''
  guestJerseyColor.value = ''
  guestJerseyNumber.value = ''
}
</script>

<template>
  <div class="ufpm-add-tester-panel">
    <div class="toggle-group" role="tablist" aria-label="Add tester source" style="margin-bottom:10px;">
      <button
        :class="['toggle-btn', { active: mode === 'roster' }]"
        type="button"
        role="tab"
        :aria-selected="mode === 'roster'"
        @click="mode = 'roster'"
      >From Roster</button>
      <button
        :class="['toggle-btn', { active: mode === 'freeform' }]"
        type="button"
        role="tab"
        :aria-selected="mode === 'freeform'"
        @click="mode = 'freeform'"
      >Free Form</button>
    </div>

    <template v-if="mode === 'roster'">
      <input
        v-model="addSearch"
        type="search"
        class="ufpm-plans-search"
        placeholder="Search roster by name or rank…"
        aria-label="Search roster to add testing member"
      />
      <div v-if="!members.length" class="ufpm-empty">No roster imported yet.</div>
      <div v-else-if="!filteredRoster.length" class="ufpm-empty">
        {{ sessionMemberIds.length >= members.length ? 'All roster members are already in this session.' : 'No matches.' }}
      </div>
      <button
        v-for="m in filteredRoster"
        :key="m.id"
        class="ufpm-plans-member-row"
        type="button"
        @click="pickRoster(m)"
      >
        <span class="ufpm-plans-member-name">{{ rosterLabel(m) }}</span>
        <span class="ufpm-plans-member-meta">{{ m.organization || '' }}</span>
      </button>
    </template>

    <template v-else>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 12px;">
        <div class="field">
          <label for="ufpm-guest-rank">Rank</label>
          <select id="ufpm-guest-rank" v-model="guestRank">
            <option value="">-- None --</option>
            <option v-for="r in RANK_OPTIONS" :key="r" :value="r">{{ r }}</option>
          </select>
        </div>
        <div class="field">
          <label for="ufpm-guest-name">Name</label>
          <input
            id="ufpm-guest-name"
            type="text"
            autocomplete="off"
            placeholder="Blank = Tester 1, 2…"
            v-model.trim="guestName"
          />
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 12px;">
        <div class="field">
          <label for="ufpm-guest-jersey-color">Jersey Color</label>
          <select id="ufpm-guest-jersey-color" v-model="guestJerseyColor">
            <option value="">-- None --</option>
            <option v-for="c in JERSEY_COLORS" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <div class="field">
          <label for="ufpm-guest-jersey-number">Jersey #</label>
          <input
            id="ufpm-guest-jersey-number"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            v-model.trim="guestJerseyNumber"
          />
        </div>
      </div>
      <button class="btn btn-primary" type="button" style="width:100%;" @click="submitGuest">Add Tester</button>
    </template>
  </div>
</template>
